import { Task, TaskGroupBy } from "@common/types/task";
import type {
    BoardLaneDef,
    BoardPlacement,
} from "@design-system/Board/Board.types";
import { getTaskGroupId } from "../taskListUtils";
import {
    buildTaskBoardColumns,
    type TaskBoardColumnsArgs,
} from "./taskBoardColumns";

/** Lanes by entity: the one swimlane grouping List has no group for. */
export const ENTITY_LANE_BY = "entity";

/** What swimlanes can show: any header grouping, or entities. */
export type TaskBoardLaneBy = TaskGroupBy | typeof ENTITY_LANE_BY;

export const NO_ENTITY_LANE_ID = "No entity";
const ENTITY_LANE_PREFIX = "entity:";

/** Prefixed, so an entity lane never shares an id with an assignee's. */
export const getEntityLaneId = (entityId: number) =>
    `${ENTITY_LANE_PREFIX}${entityId}`;

/** The entity an entity lane stands for; null for "No entity". */
export const parseEntityLaneId = (laneId: string): number | null =>
    laneId.startsWith(ENTITY_LANE_PREFIX)
        ? Number(laneId.slice(ENTITY_LANE_PREFIX.length))
        : null;

/** A lane whose title is plain text, so drops can name it before a refetch. */
export type TaskBoardLane = BoardLaneDef & { title: string };

/** The task's lanes: one per entity under Entity, else the lane of its List group. */
export const getTaskLaneIds = (laneBy: TaskBoardLaneBy, task: Task) => {
    if (laneBy !== ENTITY_LANE_BY) {
        return [getTaskGroupId(laneBy, task)];
    }
    const entityIds = task.entityIds ?? [];
    return entityIds.length === 0
        ? [NO_ENTITY_LANE_ID]
        : entityIds.map(getEntityLaneId);
};

interface EntityLanesArgs {
    tasks: readonly Task[];
    entityNamesById: ReadonlyMap<number, string>;
    isEmptyColumnsShown: boolean;
}

/**
 * Entity lanes by name, "No entity" last. With empty lanes shown, every
 * entity the page knows gets one; otherwise only those with tasks.
 */
const buildEntityLanes = ({
    tasks,
    entityNamesById,
    isEmptyColumnsShown,
}: EntityLanesArgs): TaskBoardLane[] => {
    const taskEntityIds = tasks.flatMap((task) => task.entityIds ?? []);
    const entityIds = new Set(
        isEmptyColumnsShown
            ? [...entityNamesById.keys(), ...taskEntityIds]
            : taskEntityIds,
    );
    const lanes = [...entityIds]
        .map((entityId) => ({
            id: getEntityLaneId(entityId),
            title: entityNamesById.get(entityId) ?? "Unknown entity",
        }))
        .toSorted((first, second) => first.title.localeCompare(second.title));
    const hasTaskWithoutEntity = tasks.some(
        (task) => (task.entityIds ?? []).length === 0,
    );
    return isEmptyColumnsShown || hasTaskWithoutEntity
        ? [...lanes, { id: NO_ENTITY_LANE_ID, title: "No entity" }]
        : lanes;
};

type TaskBoardLanesArgs = Omit<TaskBoardColumnsArgs, "groupBy"> & {
    laneBy: TaskBoardLaneBy | null;
    entityNamesById: ReadonlyMap<number, string>;
};

/**
 * The board's swimlanes: undefined without them. Lanes by a header grouping
 * are that grouping's columns (same ids, titles, order and empty rules).
 */
export const buildTaskBoardLanes = ({
    laneBy,
    entityNamesById,
    ...columnArgs
}: TaskBoardLanesArgs): TaskBoardLane[] | undefined => {
    if (laneBy === null) {
        return undefined;
    }
    if (laneBy === ENTITY_LANE_BY) {
        return buildEntityLanes({ ...columnArgs, entityNamesById });
    }
    return buildTaskBoardColumns({ ...columnArgs, groupBy: laneBy }).map(
        ({ id, title, icon }) => ({ id, title, icon }),
    );
};

/** One card per task and lane: in its List group's column, in each of its lanes. */
export const createTaskBoardPlacements =
    ({
        groupBy,
        laneBy,
    }: {
        groupBy: TaskGroupBy;
        laneBy: TaskBoardLaneBy | null;
    }) =>
    (task: Task): BoardPlacement[] => {
        const columnId = getTaskGroupId(groupBy, task);
        return laneBy === null
            ? [{ columnId, laneId: null }]
            : getTaskLaneIds(laneBy, task).map((laneId) => ({
                  columnId,
                  laneId,
              }));
    };
